import { useGetAccountQuery } from '@/services/accounts';
import { useGetDOEPRPricingQuery, useGetPricingQuery } from '@/services/pricing';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { AdderType, BatteryPricingMethodOption, BatteryType, ProgramType } from 'types';

import { CreateQuoteFormikContext } from '../CreateQuote';

export const useGetAccountPricing = (values?: CreateQuoteFormikContext) => {
  const { id } = useParams<{ id: any }>();
  const { data: accountData } = useGetAccountQuery(id);

  // this is only used to fetch doePR pricing
  const selectedAdders = useMemo(() => {
    const adders = [];
    if (accountData?.programType === ProgramType.doePr) {
      if (values?.electricalUpgradeIncluded) {
        adders.push(AdderType.electricalUpgrade);
      }
      if (values?.solarReadinessIncluded) {
        adders.push(AdderType.solarReadiness);
      }
      if (values?.battery) {
        if (values.battery.type === BatteryType.backupBattery) {
          if (values.battery.pricingMethod === BatteryPricingMethodOption.rawCost) {
            adders.push(AdderType.backupBattery);
          } else {
            adders.push(AdderType.backupBatteryKwh);
          }
        } else if (values.battery.type === BatteryType.arbitrageBattery) {
          if (values.battery.pricingMethod === BatteryPricingMethodOption.rawCost) {
            adders.push(AdderType.backupBattery);
          } else {
            adders.push(AdderType.backupBatteryKwh);
          }
        }
      }
    }
    return adders;
  }, [values, accountData]);

  const batteryParams = useMemo(() => {
    if (!values?.battery?.type || !values?.battery?.pricingMethod) return {};
    const params: Record<string, any> = {
      batteryType: values.battery.type,
      batteryPricingMethod: values.battery.pricingMethod,
    };
    if (Number.isFinite(values.debouncedBatteryCost) && values.debouncedBatteryCost! >= 0) {
      params.batteryCost = values.debouncedBatteryCost;
    }
    return params;
  }, [values?.battery?.type, values?.battery?.pricingMethod, values?.debouncedBatteryCost]);

  const {
    data: productsST,
    isLoading: pricingIsLoadingST,
    error: pricingLoadingErrorST,
    isFetching: pricingIsFetchingST,
  } = useGetPricingQuery(
    {
      id,
      ...batteryParams,
      electricalUpgradeIncluded: values?.electricalUpgradeIncluded,
      includeDomesticContentPricing: true,
    },
    {
      skip: !accountData?.programType || accountData?.programType === ProgramType.doePr,
    },
  );

  const {
    data: productsWithoutDcST,
    isLoading: pricingIsLoadingWithoutDcST,
    error: pricingLoadingErrorWithoutDcST,
    isFetching: pricingIsFetchingWithoutDcST,
  } = useGetPricingQuery(
    {
      id,
      ...batteryParams,
      electricalUpgradeIncluded: values?.electricalUpgradeIncluded,
      includeDomesticContentPricing: false,
    },
    {
      skip: !accountData?.programType || accountData?.programType === ProgramType.doePr,
    },
  );

  const {
    data: productsPR,
    isLoading: pricingIsLoadingPR,
    error: pricingLoadingErrorPR,
    isFetching: pricingIsFetchingPR,
  } = useGetDOEPRPricingQuery(
    {
      id,
      adders: selectedAdders,
      backupBatteryManufacturer: (values?.battery?.manufacturer as any)?.value,
      backupBatteryModelNumber: (values?.battery?.models?.[0]?.modelNumber as any)?.value,
      backupBatteryQuantity: (values?.battery?.models?.[0]?.quantity as any)?.value,
      solarReadinessCost: values?.solarReadinessCost,
    },
    {
      skip: !accountData?.programType || accountData?.programType !== ProgramType.doePr,
    },
  );

  const isPricingLoading = pricingIsLoadingST || pricingIsLoadingWithoutDcST || pricingIsLoadingPR;
  const isFetching = pricingIsFetchingST || pricingIsFetchingWithoutDcST || pricingIsFetchingPR;
  const isPricingError = pricingLoadingErrorST || pricingLoadingErrorWithoutDcST || pricingLoadingErrorPR;

  const standardSolarProducts = useMemo(() => {
    if (!productsST || !productsWithoutDcST) return [];

    const combinedProducts = productsST.reduce((acc: any, product: any) => {
      const matchingProduct = productsWithoutDcST.find((p: any) => p.productId === product.productId);
      if (!matchingProduct) return acc;
      return [
        ...acc,
        {
          ...product,
          ppwWithoutDC: matchingProduct.ppwRate,
          solarOnlyEPCPaymentWithoutDC: matchingProduct.solarOnlyEPCPayment,
          totalEPCPaymentWithoutDC: matchingProduct.totalEPCPayment,
        },
      ];
    }, []);
    return combinedProducts;
  }, [productsST, productsWithoutDcST]);

  const products: any[] | undefined =
    accountData?.programType === ProgramType.doePr ? productsPR : standardSolarProducts;

  const escalationRates = useMemo(() => {
    const rates = new Set<number>();
    if (products) {
      products.forEach((product) => {
        if (product.escalationRate !== undefined) {
          rates.add(product.escalationRate);
        }
      });
    }
    return Array.from(rates).toSorted((a: number, b: number) => a - b);
  }, [products]);

  const isDCPricingDifferent = useMemo(() => {
    const dcProduct = productsST?.find((product) => product.domesticContentEnabled);
    if (dcProduct) {
      const nonDcProduct = productsWithoutDcST?.find((product) => product.productId === dcProduct.productId);
      if (nonDcProduct) {
        return dcProduct.ppwRate !== nonDcProduct.ppwRate;
      }
    }
    return false;
  }, [productsST, productsWithoutDcST]);

  const showDCComparison = useMemo(() => {
    return productsST?.find((product) => product.domesticContentEnabled);
  }, [productsST]);

  return {
    escalationRates,
    products,
    isLoading: isPricingLoading,
    error: isPricingError,
    showDCComparison,
    isDCPricingDifferent,
    isFetching,
  };
};
